/**
 * EnvironmentalFilterDrawer — "Report Settings" drawer for environmental reports.
 *
 * 3 filter groups: Areas (area + areaTypes), Residents, Events (eventTypes + reasons).
 * No area groups, resident groups, or device categories (device cat auto-set by type).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const SECTIONS = [
    {
        id: 'areas',
        label: 'Areas',
        subs: [
            { key: 'areas', label: 'Area', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' },
            { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' },
        ],
    },
    {
        id: 'residents',
        label: 'Residents',
        subs: [
            { key: 'residents', label: 'Resident', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' },
        ],
    },
    {
        id: 'events',
        label: 'Events',
        subs: [
            { key: 'eventTypes', label: 'Event Types', idKey: 'event_type_id', nameKey: 'event_type_name', optionsKey: 'eventTypes' },
            { key: 'reasons', label: 'Reasons', idKey: 'reason_code_id', nameKey: 'reason_code_description', optionsKey: 'reasonCodes' },
        ],
    },
];

function emptyFilters() {
    return {
        areas: new Set(),
        areaTypes: new Set(),
        residents: new Set(),
        users: new Set(),
        eventTypes: new Set(),
        reasons: new Set(),
    };
}

function cloneFilters(f) {
    const result = {};
    Object.keys(f).forEach((k) => { result[k] = new Set(f[k]); });
    return result;
}

function filtersToSets(currentFilters) {
    if (!currentFilters) return emptyFilters();
    const result = {};
    Object.keys(currentFilters).forEach((k) => {
        result[k] = new Set((currentFilters[k] || []).map(String));
    });
    return result;
}

const FilterSubSection = ({ subDef, options, selected, onToggle, onSelectAll, onClear }) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const filteredOptions = useMemo(() => {
        let list = options || [];
        if (showSelectedOnly) list = list.filter((opt) => selected.has(String(opt[subDef.idKey])));
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((opt) => (opt[subDef.nameKey] || '').toLowerCase().includes(term));
        }
        return list;
    }, [options, search, subDef.idKey, subDef.nameKey, selected, showSelectedOnly]);

    return (
        <div className="ar-filter-section">
            <div className="ar-filter-section__header">
                <span className="ar-filter-section__label">{subDef.label}</span>
                <div className="ar-filter-section__actions">
                    <button className="ar-filter-section__btn" onClick={() => onSelectAll(subDef.key, (options || []).map((o) => String(o[subDef.idKey])))} type="button">Select All</button>
                    <button className="ar-filter-section__btn" onClick={() => onClear(subDef.key)} type="button">Clear</button>
                    <button className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`} onClick={() => setShowSelectedOnly((prev) => !prev)} type="button">Show Selected</button>
                </div>
            </div>
            <input className="ar-filter-section__search" type="text" placeholder={`Search ${subDef.label.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="ar-filter-list">
                {filteredOptions.length === 0 ? (<p className="ar-filter-list__empty">{showSelectedOnly ? 'No selections.' : 'No options found.'}</p>) : null}
                {filteredOptions.map((opt) => {
                    const id = String(opt[subDef.idKey]);
                    return (
                        <label key={id} className="ar-filter-list__item">
                            <input type="checkbox" checked={selected.has(id)} onChange={() => onToggle(subDef.key, id)} />
                            <span>{opt[subDef.nameKey] || '(empty)'}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

const EnvironmentalFilterDrawer = ({
    open, onClose, onApply, onClear, filterOptions, currentFilters, saving,
    timeRanges, rangeId, customRange, start, end,
    onTimeRangeChange, onCustomToggle, onStartChange, onEndChange,
    timeRangeDisabled = false,
}) => {
    const [working, setWorking] = useState(() => filtersToSets(currentFilters));
    const [expandedSections, setExpandedSections] = useState(new Set(['timeRange']));

    useEffect(() => { if (open) setWorking(filtersToSets(currentFilters)); }, [open, currentFilters]);

    const toggleSection = useCallback((id) => {
        setExpandedSections((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    }, []);

    const handleToggle = useCallback((key, id) => {
        setWorking((prev) => { const next = cloneFilters(prev); if (next[key].has(id)) next[key].delete(id); else next[key].add(id); return next; });
    }, []);

    const handleSelectAll = useCallback((key, allIds) => {
        setWorking((prev) => { const next = cloneFilters(prev); next[key] = new Set(allIds); return next; });
    }, []);

    const handleClear = useCallback((key) => {
        setWorking((prev) => { const next = cloneFilters(prev); next[key] = new Set(); return next; });
    }, []);

    const getSectionCount = useCallback((section) => {
        let count = 0;
        section.subs.forEach((sub) => { count += (working[sub.key]?.size || 0); });
        return count;
    }, [working]);

    const handleApply = useCallback(() => {
        const result = {};
        Object.keys(working).forEach((k) => { result[k] = [...working[k]]; });
        onApply(result);
    }, [working, onApply]);

    const handleClearAll = useCallback(() => { setWorking(emptyFilters()); onClear(); }, [onClear]);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--xl${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Report Settings</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>
                    {/* Time Range */}
                    <div className={`ar-accordion${timeRangeDisabled ? ' er-time-disabled' : ''}`}>
                        <button className={`ar-accordion__header${expandedSections.has('timeRange') ? ' ar-accordion__header--open' : ''}`} onClick={() => toggleSection('timeRange')} type="button">
                            <span className="ar-accordion__icon">{expandedSections.has('timeRange') ? '\u25BC' : '\u25B6'}</span>
                            <span className="ar-accordion__label">Time Range</span>
                            <span className="ar-accordion__count">{timeRangeDisabled ? 'Today (fixed)' : customRange ? 'Custom' : (timeRanges || []).find((r) => r.id === rangeId)?.label || 'Yesterday'}</span>
                        </button>
                        {expandedSections.has('timeRange') ? (
                            <div className="ar-accordion__body">
                                <div className="ar-time-bar">
                                    <div className="ar-time-bar__ranges">
                                        {(timeRanges || []).map((range) => (
                                            <button key={range.id} className={`ar-time-btn${rangeId === range.id ? ' ar-time-btn--active' : ''}`} onClick={() => onTimeRangeChange(range)} type="button" disabled={timeRangeDisabled}>{range.label}</button>
                                        ))}
                                        <button className={`ar-time-btn${customRange ? ' ar-time-btn--active' : ''}`} onClick={onCustomToggle} type="button" disabled={timeRangeDisabled}>Custom</button>
                                    </div>
                                    {customRange && !timeRangeDisabled ? (
                                        <div className="ar-custom-range">
                                            <DatePicker selected={start} onChange={onStartChange} dateFormat="MM/dd/yyyy" className="ar-custom-range__input" placeholderText="Start date" selectsStart startDate={start} endDate={end} maxDate={end} />
                                            <span className="ar-custom-range__sep">to</span>
                                            <DatePicker selected={end} onChange={onEndChange} dateFormat="MM/dd/yyyy" className="ar-custom-range__input" placeholderText="End date" selectsEnd startDate={start} endDate={end} minDate={start} />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Filter sections */}
                    {SECTIONS.map((section) => {
                        const isExpanded = expandedSections.has(section.id);
                        const count = getSectionCount(section);
                        return (
                            <div key={section.id} className="ar-accordion">
                                <button className={`ar-accordion__header${isExpanded ? ' ar-accordion__header--open' : ''}`} onClick={() => toggleSection(section.id)} type="button">
                                    <span className="ar-accordion__icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                                    <span className="ar-accordion__label">{section.label}</span>
                                    {count > 0 ? (<span className="ar-accordion__count">{count} selected</span>) : null}
                                </button>
                                {isExpanded ? (
                                    <div className="ar-accordion__body">
                                        {section.subs.map((sub) => (
                                            <FilterSubSection key={sub.key} subDef={sub} options={filterOptions?.[sub.optionsKey] || []} selected={working[sub.key] || new Set()} onToggle={handleToggle} onSelectAll={handleSelectAll} onClear={handleClear} />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} disabled={saving} type="button">Clear All</button>
                    <button className="dp-btn dp-btn--primary" onClick={handleApply} disabled={saving} type="button">{saving ? 'Applying\u2026' : 'Apply Settings'}</button>
                </div>
            </div>
        </>
    );
};

export default EnvironmentalFilterDrawer;
